// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import handler from '../../../../share/util/apiResultValidator';
import customTable from '../../../../share/components/vue/PaginationTable';
import loading from '../../../../share/components/vue/ProcessingIcon';
import diagnoseResource from '../../../../share/util/diagnose';
import ApiService from '../../../../share/util/api/service';

const i18nVehicleInfoKey = 'vehicle_info_name';
const i18nInfoTypeKey = 'info_type';
const i18nUnitKey = 'obd_unit';

export default {
  name: "obdVehicleInfoPage",
  props: {
    // デバッグ環境のみの動作確認
    testModeVisible: { type: Boolean, required: false }
  },
  data() {
    return {
      processing: false,
      recollectedData: [],
      showData: null,
      isMemoDialogLoading: false
    };
  },

  components: {
    'custom-table': customTable,
    'processing-view': loading,
  },

  methods: {
    ...mapActions('auth', ['logout']),

    /**
     * 車両情報取得計測データ一覧取得
     * @param {String} obdDataId OBD Data ID
     * @param {String} measuresId 計測データID
     */
    async createObdVehicleInfoMeasuresList(obdDataId, measuresId) {
      try {
        this.processing = true;

        const params = { region_info: this.cachedProjectDataSource };
        const apiResponse = await ApiService.getParamCall(
          ApiService.OBD,
          `/obd/${obdDataId}/measures/${measuresId}/vehicleInfoList`,
          params,
          this.operationScreen
        );

        handler.validate(handler.validateTypes.all, apiResponse, this, null, () => {
          // 表示データを取得
          const dispData = apiResponse.data.dispList
            .map(item => {
              let obj = null;
              // "00","20" でも "F800","F820" でもない(20の倍数)場合のみ obj を作成
              const vehicle_info = parseInt(item.vehicle_info, 16);
              if (vehicle_info % 32 !== 0) {
                obj = {
                  vehicle_info: "＜" + this.$t(`${i18nVehicleInfoKey}.${item.vehicle_info}`) + "＞",
                  body: item.body.map(bodyItem => ({
                    item_name: this.$t(`${i18nInfoTypeKey}.${bodyItem.item_name}`),
                    value: bodyItem.value === null ? '' : bodyItem.value,
                    unit: bodyItem.unit === '' ? '' : this.$t(`${i18nUnitKey}.${bodyItem.unit}`),
                  }))
                };
              }
              return obj;
            })
            // null を取り除く
            .filter(item => item !== null);
          this.showData = dispData;
        }, (result) => {
          if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
        });
      } finally {
        this.processing = false;
      }
    },

    /**
     * メモボタン押下.
     * DataRecollection.jsのopenMemoModalを呼び出している
     */
    async parentOpenMemoModal() {
      this.$emit('openMemoModal');
    },

    /** DataRecollection.jsの関数を呼び出す */
    async debugExport() {
      await this.dtcTestDataExport();
    },

    /** DTCテストデータエクスポート  */
    dtcTestDataExport() {
      this.$emit('dtcTestDataExport', this.recollectedData);
    }
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      cachedProjectDataSource: state => state.cache.projectDataSource,
    }),
    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    }
  },
};